/**
 * Matrix CSV export + import. One row per (slot, claim) so
 * multi-device slots fan out into multiple rows.
 *
 * Export: spreadsheet-friendly — no embedded newlines, no
 * leading-character formula injection.
 *
 * Import: parses the same CSV format back into a MatrixLibrary,
 * reconstructing devices from the (device, deviceColor) columns and
 * slots from the (kind, channel, ccNumber/noteLabel) tuples. The
 * `sharedWith` column is purely informational on export; on import,
 * actual share state derives from rows sharing the same slot tuple.
 */

import {
  type MatrixDevice,
  type MatrixDeviceColor,
  type MatrixSlot,
  type MatrixSlotKind,
  type MatrixLibrary,
  MATRIX_SCHEMA_VERSION,
  deviceForClaim,
} from '@opbuttons/schema';

function csvEscape(v: string | number | undefined): string {
  if (v === undefined || v === null) return '';
  const s = String(v);
  // Quote if the value contains comma, quote, newline, or starts with
  // characters Excel might interpret as a formula (=, +, -, @).
  const risky = /[",\n\r]/.test(s) || /^[=+\-@]/.test(s);
  if (!risky) return s;
  return `"${s.replace(/"/g, '""')}"`;
}

export function buildMatrixCsv(
  library: { devices: MatrixDevice[]; slots: MatrixSlot[] }
): string {
  const lines: string[] = [];
  lines.push([...REQUIRED_HEADER, PC_COLUMN].join(','));

  // Stable sort: kind → channel → number → device name.
  const kindRank: Record<string, number> = { cc: 0, note: 1, pc: 2 };
  const slotsSorted = library.slots.slice().sort((a, b) => {
    if (a.kind !== b.kind) return (kindRank[a.kind] ?? 9) - (kindRank[b.kind] ?? 9);
    if (a.channel !== b.channel) return a.channel - b.channel;
    if (a.kind === 'cc') return (a.ccNumber ?? 0) - (b.ccNumber ?? 0);
    if (a.kind === 'pc') return (a.pcNumber ?? 0) - (b.pcNumber ?? 0);
    return (a.noteMidi ?? 0) - (b.noteMidi ?? 0);
  });

  for (const slot of slotsSorted) {
    for (const claim of slot.claims) {
      const device = deviceForClaim(library as any, claim);
      const otherDevices = slot.claims
        .filter((c) => c.deviceId !== claim.deviceId)
        .map((c) => deviceForClaim(library as any, c)?.name ?? '?')
        .join(' | ');
      lines.push([
        csvEscape(slot.kind),
        csvEscape(slot.channel),
        csvEscape(slot.kind === 'cc' ? slot.ccNumber : ''),
        csvEscape(slot.kind === 'note' ? slot.noteLabel : ''),
        csvEscape(slot.kind === 'note' ? slot.noteMidi : ''),
        csvEscape(device?.name ?? '?'),
        csvEscape(device?.color ?? ''),
        csvEscape(claim.controlLabel),
        csvEscape(otherDevices),
        csvEscape(slot.kind === 'pc' ? slot.pcNumber : ''),
      ].join(','));
    }
  }
  return lines.join('\n') + '\n';
}

// ─── CSV parser (RFC-4180-ish) ──────────────────────────────────────
// Handles quoted fields, escaped quotes, and embedded commas. Does NOT
// handle embedded newlines inside fields — our exporter never emits
// those, and import is for files exported by us (or hand-edited to the
// same shape). If someone hand-rolls a CSV with embedded newlines,
// they'll see misparsed rows + the validator catches and reports it.

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  let i = 0;
  while (i < line.length) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      cur += ch;
      i++;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (ch === ',') {
      out.push(cur);
      cur = '';
      i++;
      continue;
    }
    cur += ch;
    i++;
  }
  out.push(cur);
  return out;
}

/**
 * The nine columns every file must have, in order. Frozen: a matrix CSV the
 * operator exported before 2026-08-10 has exactly these and must keep
 * importing, so the strict header check only ever covers this prefix.
 */
const REQUIRED_HEADER = [
  'kind', 'channel', 'ccNumber', 'noteLabel', 'noteMidi',
  'device', 'deviceColor', 'controlLabel', 'sharedWith',
];

/**
 * Tenth column, added with the `pc` slot kind. OPTIONAL on import and always
 * written on export.
 *
 * Appended rather than slotted in beside `ccNumber` for one reason: column
 * ORDER is the contract with every file already on disk, and inserting a
 * column would silently shift `device` into `deviceColor` for all of them -
 * an import that "succeeds" while scrambling the rig map is far worse than
 * one that refuses. A pc row in a hand-authored nine-column file still works;
 * the parser falls back to the `ccNumber` column, which is the number column
 * a human would have reached for anyway.
 */
const PC_COLUMN = 'pcNumber';

const VALID_DEVICE_COLORS: ReadonlySet<MatrixDeviceColor> = new Set([
  'sky', 'orchid', 'amber', 'lime', 'rose', 'forest', 'cyan', 'magenta',
]);

export type ParseMatrixCsvResult =
  | { ok: true; library: MatrixLibrary; warnings: string[] }
  | { ok: false; reason: string };

/** Parse a CSV string (in the format produced by buildMatrixCsv) into
 *  a fresh MatrixLibrary. Reconstructs devices from the device-name
 *  columns + slots from (kind, channel, cc/note) tuples. Each parsed
 *  row contributes one claim to its slot; rows sharing the same slot
 *  tuple stack their claims on the same slot. */
export function parseMatrixCsv(text: string): ParseMatrixCsvResult {
  // Strip BOM if Excel added one.
  let body = text;
  if (body.charCodeAt(0) === 0xFEFF) body = body.slice(1);
  const lines = body.split(/\r?\n/).filter((l) => l.length > 0);
  if (lines.length === 0) {
    return { ok: false, reason: 'File is empty.' };
  }

  const headerCells = parseCsvLine(lines[0]!);
  // Only the nine REQUIRED columns are checked, so a file exported before the
  // pc column existed still imports untouched.
  for (let i = 0; i < REQUIRED_HEADER.length; i++) {
    if (headerCells[i] !== REQUIRED_HEADER[i]) {
      return {
        ok: false,
        reason:
          `Header mismatch at column ${i + 1}: expected "${REQUIRED_HEADER[i]}"`
          + `, got "${headerCells[i] ?? '(missing)'}".`
          + ` Use a file exported from OPButtons's Export matrix.`,
      };
    }
  }
  // A tenth column is allowed, but only if it is the one we know about -
  // anything else means the columns have shifted and every row after it would
  // be misread, which is exactly the failure this refuses to perform quietly.
  if (headerCells.length > REQUIRED_HEADER.length && headerCells[REQUIRED_HEADER.length] !== PC_COLUMN) {
    return {
      ok: false,
      reason:
        `Unexpected column ${REQUIRED_HEADER.length + 1}: `
        + `"${headerCells[REQUIRED_HEADER.length]}" (expected "${PC_COLUMN}" or nothing).`,
    };
  }
  const hasPcColumn = headerCells[REQUIRED_HEADER.length] === PC_COLUMN;

  const warnings: string[] = [];
  const devicesByName = new Map<string, MatrixDevice>();
  // Slot key: `${kind}|${channel}|${ccNumber or noteLabel}`
  const slotsByKey = new Map<string, MatrixSlot>();

  let nextDeviceCounter = 0;
  let nextSlotCounter = 0;
  let nextClaimCounter = 0;

  function ulid(prefix: string, n: number): string {
    // Deterministic test-friendly id rather than real ULID — matches the
    // import-matrix-csv.mjs script's approach (re-importable file produces
    // stable ids, useful for diffing test bundles). Real runtime ids are
    // produced via ulid() in the rest of the app; CSV import is rare so
    // the determinism is more useful than randomness.
    return `${prefix}-${String(n).padStart(8, '0')}`;
  }

  function getOrCreateDevice(name: string, color: string): MatrixDevice {
    const existing = devicesByName.get(name);
    if (existing) return existing;
    const safeColor: MatrixDeviceColor = (
      VALID_DEVICE_COLORS.has(color as MatrixDeviceColor) ? color : 'sky'
    ) as MatrixDeviceColor;
    if (!VALID_DEVICE_COLORS.has(color as MatrixDeviceColor) && color !== '') {
      warnings.push(`Unknown device color "${color}" for "${name}" - defaulting to sky.`);
    }
    const d: MatrixDevice = {
      id: ulid('dev', ++nextDeviceCounter),
      name,
      color: safeColor,
    };
    devicesByName.set(name, d);
    return d;
  }

  for (let lineIx = 1; lineIx < lines.length; lineIx++) {
    const raw = lines[lineIx]!;
    const cells = parseCsvLine(raw);
    if (cells.length < REQUIRED_HEADER.length) {
      warnings.push(`Line ${lineIx + 1}: only ${cells.length} columns - skipped.`);
      continue;
    }
    const [
      kindCell, channelCell, ccCell, noteLabelCell, noteMidiCell,
      deviceCell, deviceColorCell, controlLabelCell,
      // sharedWith intentionally ignored — share state derives from slot dedupe
    ] = cells;

    const pcCell = hasPcColumn ? cells[REQUIRED_HEADER.length] : undefined;

    const kind = (kindCell ?? '').trim() as MatrixSlotKind;
    if (kind !== 'cc' && kind !== 'note' && kind !== 'pc') {
      warnings.push(`Line ${lineIx + 1}: unknown kind "${kindCell}" - skipped.`);
      continue;
    }
    const channel = parseInt((channelCell ?? '').trim(), 10);
    if (!Number.isFinite(channel) || channel < 1 || channel > 16) {
      warnings.push(`Line ${lineIx + 1}: invalid channel "${channelCell}" - skipped.`);
      continue;
    }
    const deviceName = (deviceCell ?? '').trim();
    if (!deviceName || deviceName === '?') {
      warnings.push(`Line ${lineIx + 1}: missing device - skipped.`);
      continue;
    }
    const controlLabel = (controlLabelCell ?? '').trim();
    if (!controlLabel) {
      warnings.push(`Line ${lineIx + 1}: missing controlLabel - skipped.`);
      continue;
    }

    // Slot key + lookup/create.
    let slotKey: string;
    let ccNumber: number | undefined;
    let noteLabel: string | undefined;
    let noteMidi: number | undefined;
    let pcNumber: number | undefined;

    if (kind === 'pc') {
      // Prefer the dedicated column; fall back to the ccNumber column so a pc
      // row hand-written into a pre-2026-08-10 nine-column file still imports
      // rather than being silently dropped.
      const raw = (pcCell ?? '').trim() || (ccCell ?? '').trim();
      const pc = parseInt(raw, 10);
      if (!Number.isFinite(pc) || pc < 0 || pc > 127) {
        warnings.push(`Line ${lineIx + 1}: invalid program number "${raw}" - skipped.`);
        continue;
      }
      pcNumber = pc;
      slotKey = `pc|${channel}|${pc}`;
    } else if (kind === 'cc') {
      const cc = parseInt((ccCell ?? '').trim(), 10);
      if (!Number.isFinite(cc) || cc < 0 || cc > 127) {
        warnings.push(`Line ${lineIx + 1}: invalid CC number "${ccCell}" - skipped.`);
        continue;
      }
      ccNumber = cc;
      slotKey = `cc|${channel}|${cc}`;
    } else {
      const nl = (noteLabelCell ?? '').trim();
      if (!nl) {
        warnings.push(`Line ${lineIx + 1}: missing noteLabel for note kind - skipped.`);
        continue;
      }
      noteLabel = nl;
      const nmRaw = (noteMidiCell ?? '').trim();
      if (nmRaw) {
        const nm = parseInt(nmRaw, 10);
        if (Number.isFinite(nm) && nm >= 0 && nm <= 127) {
          noteMidi = nm;
        }
      }
      slotKey = `note|${channel}|${nl}`;
    }

    const device = getOrCreateDevice(deviceName, (deviceColorCell ?? '').trim());

    let slot = slotsByKey.get(slotKey);
    if (!slot) {
      const newSlot: MatrixSlot = {
        id: ulid('slot', ++nextSlotCounter),
        kind,
        channel,
        ...(ccNumber !== undefined ? { ccNumber } : {}),
        ...(noteLabel !== undefined ? { noteLabel } : {}),
        ...(noteMidi !== undefined ? { noteMidi } : {}),
        ...(pcNumber !== undefined ? { pcNumber } : {}),
        claims: [],
      };
      slotsByKey.set(slotKey, newSlot);
      slot = newSlot;
    }
    slot.claims.push({
      id: ulid('cla', ++nextClaimCounter),
      deviceId: device.id,
      controlLabel,
    });
  }

  const library: MatrixLibrary = {
    schemaVersion: MATRIX_SCHEMA_VERSION,
    devices: Array.from(devicesByName.values()),
    slots: Array.from(slotsByKey.values()),
  };
  return { ok: true, library, warnings };
}

/** Merge two matrix libraries: existing devices/slots stay, new ones
 *  are appended; slots with the same (kind, channel, cc/note) tuple
 *  combine their claims (de-duped by device + controlLabel). Used by
 *  the "merge" branch of Import matrix. */
export function mergeMatrixLibraries(
  base: MatrixLibrary,
  incoming: MatrixLibrary,
): MatrixLibrary {
  // De-dup incoming devices by name. Existing devices keep their id; a
  // new incoming device with a same-name match adopts the existing id
  // so its claims attach to the right device.
  const devicesByName = new Map<string, MatrixDevice>();
  for (const d of base.devices) devicesByName.set(d.name, d);
  const incomingDeviceIdMap = new Map<string, string>();
  for (const d of incoming.devices) {
    const existing = devicesByName.get(d.name);
    if (existing) {
      incomingDeviceIdMap.set(d.id, existing.id);
    } else {
      // Re-key the incoming device with a fresh-ish id only if it
      // collides with an existing-by-id; otherwise keep it.
      const idCollides = base.devices.some((bd) => bd.id === d.id);
      const newId = idCollides ? `${d.id}-i` : d.id;
      if (idCollides) incomingDeviceIdMap.set(d.id, newId);
      devicesByName.set(d.name, { ...d, id: newId });
    }
  }
  const mergedDevices = Array.from(devicesByName.values());

  // Slot dedupe: (kind, channel, cc|noteLabel) tuple → one slot. For
  // collisions, append incoming claims (rewritten with mapped device
  // ids) onto the existing slot; skip exact (deviceId+controlLabel)
  // duplicates.
  function slotKey(s: MatrixSlot): string {
    if (s.kind === 'cc') return `cc|${s.channel}|${s.ccNumber}`;
    if (s.kind === 'pc') return `pc|${s.channel}|${s.pcNumber}`;
    return `note|${s.channel}|${s.noteLabel}`;
  }
  const slotsByKey = new Map<string, MatrixSlot>();
  for (const s of base.slots) slotsByKey.set(slotKey(s), s);

  for (const s of incoming.slots) {
    const remappedClaims = s.claims.map((c) => ({
      ...c,
      deviceId: incomingDeviceIdMap.get(c.deviceId) ?? c.deviceId,
    }));
    const key = slotKey(s);
    const existing = slotsByKey.get(key);
    if (!existing) {
      slotsByKey.set(key, { ...s, claims: remappedClaims });
      continue;
    }
    // Merge: append claims not already present.
    for (const c of remappedClaims) {
      const dup = existing.claims.some(
        (ec) => ec.deviceId === c.deviceId && ec.controlLabel === c.controlLabel,
      );
      if (!dup) existing.claims.push(c);
    }
  }

  return {
    schemaVersion: MATRIX_SCHEMA_VERSION,
    devices: mergedDevices,
    slots: Array.from(slotsByKey.values()),
  };
}
